import { getObservablePluginLinks } from '@grafana/runtime';
// biome-ignore lint/suspicious/noDeprecatedImports: throwError and timer have deprecated overloads but we use the non-deprecated factory/single-arg forms
import { catchError, defer, map, type Observable, of, retry, throwError, timer } from 'rxjs';

export const ASSISTANT_PLUGIN_ID = 'grafana-assistant-app';
export const ASSISTANT_PLUGIN_TITLE = 'Grafana Assistant';

const MAX_RETRY_ATTEMPTS = 10;
const INITIAL_RETRY_DELAY = 100; // 100ms
const MAX_RETRY_DELAY = 5000; // 5 seconds

/**
 * Check if the assistant is available.
 * @returns An observable that emits true if the assistant is available, false otherwise.
 */
export function isAssistantAvailable(): Observable<boolean> {
  // this is to avoid crashing in older Grafana versions
  if (!getObservablePluginLinks) {
    return of(false);
  }

  // Use defer to make the call lazy and handle both sync and async errors
  return defer(() => {
    try {
      const links = getObservablePluginLinks({
        extensionPointId: 'grafana/extension-sidebar/v0-alpha',
      });
      return links.pipe(
        map((links: Array<{ pluginId: string; title: string }>) =>
          links.some((link) => link.pluginId === ASSISTANT_PLUGIN_ID && link.title === ASSISTANT_PLUGIN_TITLE)
        )
      );
    } catch (error) {
      // If getObservablePluginLinks throws synchronously, convert to observable error
      return throwError(() => error);
    }
  }).pipe(
    retry({
      count: MAX_RETRY_ATTEMPTS,
      delay: (error, retryCount) => {
        // Check if error indicates Grafana hasn't started yet
        const errorMessage = error?.message?.toLowerCase() || '';
        const isGrafanaNotReady =
          errorMessage.includes('can only be used after') ||
          errorMessage.includes('grafana instance has started') ||
          errorMessage.includes('not started');

        // Don't retry for non-race-condition errors
        if (!isGrafanaNotReady) {
          throw error;
        }

        // Exponential backoff: delay = INITIAL_RETRY_DELAY * 2^retryCount, capped at MAX_RETRY_DELAY
        const delayMs = Math.min(INITIAL_RETRY_DELAY * 2 ** (retryCount - 1), MAX_RETRY_DELAY);
        return timer(delayMs);
      },
    }),
    catchError(() => {
      // After all retries exhausted, return false
      return of(false);
    })
  );
}
